'use client';

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client/react';
import {
  DOCUMENTS_QUERY,
  DOCUMENT_QUERY,
  DOCUMENTS_EXPORT_QUERY,
  DOCUMENTS_FOR_EXPORT_QUERY,
  DOCUMENT_BULK_PDF_URL_QUERY,
  SET_DOCUMENTS_READ_MUTATION,
  type Document,
  type DocumentConnection,
  type DocumentFilterInput,
  type DocumentType,
  DocumentType as DocumentTypeEnum,
  SortField,
  SortDirection,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import {
  useAdmin,
  useSessionNeedsRenewal,
  SessionRenewalNotice,
} from '@paeffgen/react/crm-documents/feature-auth';
import { Skeleton, EmptyState, SearchHighlightProvider, SelectAllToggle } from '@paeffgen/react/crm-documents/ui';
import { exportToCsv, exportLineItemsToCsv, useSelection, useRememberedFlag } from '@paeffgen/react/crm-documents/util';
import { DocumentRow } from './document-row';
import { DocumentDetailDialog } from './document-detail-dialog';
import { BelegeExportBar } from './belege-export-bar';
import { DocumentFilters, type FilterState } from './document-filters';
import { DocumentFilterChips } from './document-filter-chips';
import { InvoiceTotalsBar } from './invoice-totals-bar';
import { PositionsToggle, resolvePositionsView } from './positions-toggle';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  CheckSquare,
  FileX,
  X,
} from 'lucide-react';
import { clsx } from 'clsx';
import { format } from 'date-fns';

// ============================================================================
// Sort helper components
// ============================================================================

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

function SortIcon({
  columnKey,
  sortConfig,
}: {
  columnKey: string;
  sortConfig: SortConfig;
}) {
  if (sortConfig.key !== columnKey)
    return <ArrowUpDown className="w-4 h-4 ml-1 text-gray-400" />;
  return sortConfig.direction === 'asc' ? (
    <ArrowUp className="w-4 h-4 ml-1 text-primary" />
  ) : (
    <ArrowDown className="w-4 h-4 ml-1 text-primary" />
  );
}

function HeaderCell({
  label,
  columnKey,
  sortConfig,
  onSort,
  alignRight = false,
  className = '',
}: {
  label: string;
  columnKey: string;
  sortConfig: SortConfig;
  onSort: (key: string) => void;
  alignRight?: boolean;
  className?: string;
}) {
  return (
    <div
      className={clsx(
        'flex items-center cursor-pointer hover:bg-gray-100 p-2 -m-2 rounded transition-colors select-none',
        alignRight && 'justify-end',
        className
      )}
      onClick={() => onSort(columnKey)}
    >
      <span>{label}</span>
      <SortIcon columnKey={columnKey} sortConfig={sortConfig} />
    </div>
  );
}

// ============================================================================
// Map local sort keys to GraphQL SortField
// ============================================================================

function toSortField(key: string): SortField {
  switch (key) {
    case 'date':
      return SortField.DATE;
    case 'number':
      return SortField.DOCUMENT_NUMBER;
    case 'netTotal':
      return SortField.NET_TOTAL;
    case 'commission':
      return SortField.COMMISSION;
    default:
      return SortField.DATE;
  }
}

function toSortDirection(dir: 'asc' | 'desc'): SortDirection {
  return dir === 'asc' ? SortDirection.ASC : SortDirection.DESC;
}

// ============================================================================
// Build GraphQL filter from URL state
// ============================================================================

function buildFilter(filters: FilterState): DocumentFilterInput {
  const gqlFilter: DocumentFilterInput = {};

  if (filters.search) {
    gqlFilter.searchText = filters.search;
  }

  if (filters.type.length > 0) {
    gqlFilter.types = filters.type;
  }

  if (filters.dateRange !== 'ALL') {
    const now = new Date();
    if (filters.dateRange === 'CUSTOM') {
      if (filters.startDate) gqlFilter.dateFrom = filters.startDate;
      if (filters.endDate) gqlFilter.dateTo = filters.endDate;
    } else {
      let cutoff: Date;
      switch (filters.dateRange) {
        case '30DAYS':
          cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case '3MONTHS':
          cutoff = new Date(now);
          cutoff.setMonth(cutoff.getMonth() - 3);
          break;
        case '6MONTHS':
          cutoff = new Date(now);
          cutoff.setMonth(cutoff.getMonth() - 6);
          break;
        case '1YEAR':
          cutoff = new Date(now);
          cutoff.setFullYear(cutoff.getFullYear() - 1);
          break;
        default:
          cutoff = new Date(0);
      }
      gqlFilter.dateFrom = cutoff.toISOString().split('T')[0];
    }
  }

  if (filters.minAmount) {
    gqlFilter.minAmount = parseFloat(filters.minAmount);
  }
  if (filters.maxAmount) {
    gqlFilter.maxAmount = parseFloat(filters.maxAmount);
  }
  if (filters.location) {
    gqlFilter.locationCode = filters.location;
  }
  if (filters.commission) {
    gqlFilter.commission = filters.commission;
  }
  if (filters.yourReference) {
    gqlFilter.yourReference = filters.yourReference;
  }

  return gqlFilter;
}

// ============================================================================
// Main Component
// ============================================================================

const PAGE_SIZE = 20;
const EXPORT_LIMIT = 1000;

export function DocumentList() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { adminQueryVars } = useAdmin();
  const needsRenewal = useSessionNeedsRenewal();

  // ------------------------------------------------------------------
  // Filters from URL
  // ------------------------------------------------------------------
  const filters: FilterState = {
    search: searchParams.get('search') || '',
    type:
      (searchParams
        .get('type')
        ?.split(',')
        .filter((t) =>
          Object.values(DocumentTypeEnum).includes(t as DocumentType)
        ) as DocumentType[]) || [],
    dateRange:
      (searchParams.get('dateRange') as FilterState['dateRange']) || 'ALL',
    startDate: searchParams.get('startDate') || undefined,
    endDate: searchParams.get('endDate') || undefined,
    minAmount: searchParams.get('minAmount') || undefined,
    maxAmount: searchParams.get('maxAmount') || undefined,
    location: searchParams.get('location') || undefined,
    commission: searchParams.get('commission') || undefined,
    yourReference: searchParams.get('yourReference') || undefined,
  };

  // Zuschaltbare Positionsansicht. Der URL-Parameter ist die Wahrheit; ein
  // gemerkter Wunsch wird unten nur angewandt, wenn die URL nichts sagt.
  // Der Parameter ist dreiwertig ('1' | '0' | nicht gesetzt) — die
  // Anwesenheit muss separat vom Wahrheitswert mitgeführt werden. Sonst
  // ließe updateUrl den ausdrücklichen AUS-Zustand ('0') beim nächsten
  // Filterwechsel fallen, weil dessen Vorbelegung nur noch `false` sähe —
  // und danach griffe der gemerkte Wunsch wieder (Ansicht kippt beim
  // Entfernen eines Filter-Chips zurück auf "ein").
  const urlPositionsParam: boolean | null = searchParams.has('positionen')
    ? searchParams.get('positionen') === '1'
    : null;

  // ------------------------------------------------------------------
  // Sort from URL
  // ------------------------------------------------------------------
  const sortConfig: SortConfig = useMemo(() => {
    const validKeys = ['date', 'number', 'netTotal', 'commission'];
    const key = searchParams.get('sort') ?? 'date';
    const direction = searchParams.get('dir') === 'asc' ? 'asc' : 'desc';
    return { key: validKeys.includes(key) ? key : 'date', direction };
  }, [searchParams]);

  /** Write filter + sort + positions params to URL in one go. */
  const updateUrl = useCallback(
    (
      newFilters: FilterState,
      sort: SortConfig,
      positions: boolean | null = urlPositionsParam,
    ) => {
      const params = new URLSearchParams();
      if (newFilters.search) params.set('search', newFilters.search);
      if (newFilters.type.length > 0)
        params.set('type', newFilters.type.join(','));
      if (newFilters.dateRange !== 'ALL')
        params.set('dateRange', newFilters.dateRange);
      if (newFilters.startDate) params.set('startDate', newFilters.startDate);
      if (newFilters.endDate) params.set('endDate', newFilters.endDate);
      if (newFilters.minAmount) params.set('minAmount', newFilters.minAmount);
      if (newFilters.maxAmount) params.set('maxAmount', newFilters.maxAmount);
      if (newFilters.location) params.set('location', newFilters.location);
      if (newFilters.commission)
        params.set('commission', newFilters.commission);
      if (newFilters.yourReference)
        params.set('yourReference', newFilters.yourReference);
      // Sort — only write non-defaults to keep URL clean
      if (sort.key !== 'date') params.set('sort', sort.key);
      if (sort.direction !== 'desc') params.set('dir', sort.direction);
      // Dreiwertig: nur schreiben, wenn ausdrücklich etwas gesagt wurde
      // (true/false), und nur solange ein Suchbegriff aktiv ist — sonst
      // bliebe "positionen=1" nach dem Löschen des Suchbegriffs stehen und
      // schaltete sich bei der nächsten Suche automatisch wieder ein.
      if (positions != null && newFilters.search) {
        params.set('positionen', positions ? '1' : '0');
      }

      router.replace(`?${params.toString()}`, { scroll: false });
    },
    [router, urlPositionsParam]
  );

  const { remembered, remember } = useRememberedFlag('crm:belege:positionen');

  const positionsView = resolvePositionsView({
    urlHasParam: urlPositionsParam !== null,
    urlValue: urlPositionsParam === true,
    remembered,
    hasSearch: Boolean(filters.search),
  });

  const handleTogglePositions = useCallback(
    (next: boolean) => {
      remember(next);
      updateUrl(filters, sortConfig, next);
    },
    [remember, updateUrl, filters, sortConfig]
  );

  // Den gemerkten Wunsch einmalig in die URL nachziehen. Das Ref verhindert,
  // dass ein späteres Abwählen durch den Effekt sofort wieder überschrieben
  // wird — nach dem ersten Nachziehen steht der Parameter in der URL und die
  // Regel oben liefert shouldWriteUrl = false.
  const appliedRemembered = useRef(false);
  useEffect(() => {
    if (appliedRemembered.current) return;
    if (!positionsView.shouldWriteUrl) return;
    appliedRemembered.current = true;
    updateUrl(filters, sortConfig, true);
  }, [positionsView.shouldWriteUrl, filters, sortConfig, updateUrl]);

  // Mehrfachauswahl (geteilter Hook — einheitlich mit Favoriten/Projekte/Artikelsuche).
  // Auswahl bleibt über Sortier-/Seitenwechsel erhalten, wird aber bei Filterwechsel
  // (setFilters) bewusst verworfen.
  const sel = useSelection();

  const setFilters = useCallback(
    (newFilters: FilterState) => {
      sel.clear();
      updateUrl(newFilters, sortConfig);
    },
    [updateUrl, sortConfig, sel.clear]
  );

  const handleSort = useCallback(
    (key: string) => {
      const newSort: SortConfig = {
        key,
        direction:
          sortConfig.key === key && sortConfig.direction === 'asc'
            ? 'desc'
            : 'asc',
      };
      updateUrl(filters, newSort);
    },
    [updateUrl, filters, sortConfig]
  );

  const [openDocumentId, setOpenDocumentId] = useState<string | null>(
    searchParams.get('open')
  );

  // Sync openDocumentId with the `?open=` URL param when it actually changes
  // (e.g. when the user clicks a document-chain link). Manual row-toggle does
  // not update the URL, so this effect won't override that local state — it
  // only reacts to genuine URL transitions.
  const lastUrlOpenRef = useRef<string | null>(searchParams.get('open'));
  useEffect(() => {
    const current = searchParams.get('open');
    if (current !== lastUrlOpenRef.current) {
      lastUrlOpenRef.current = current;
      setOpenDocumentId(current);
    }
  }, [searchParams]);

  const hasActiveFilters = !!(
    filters.search ||
    filters.type.length > 0 ||
    filters.dateRange !== 'ALL' ||
    filters.minAmount ||
    filters.maxAmount ||
    filters.location ||
    filters.commission ||
    filters.yourReference ||
    filters.startDate ||
    filters.endDate
  );

  // ------------------------------------------------------------------
  // Apollo Query
  // ------------------------------------------------------------------
  const gqlFilter = useMemo(() => buildFilter(filters), [filters]);

  // Rechnungssummen-Card nur zeigen, wenn Rechnungen Teil der gefilterten
  // Sicht sind: „Alle" (kein Typ-Filter), „Rechnung" oder „Rechnung +
  // Gutschrift". Wer explizit nur Gutschriften/Angebote/Aufträge/
  // Lieferscheine filtert, bekäme sonst eine Kennzahl zu Belegen angezeigt,
  // die die Liste gerade gar nicht enthält.
  const showInvoiceTotals =
    filters.type.length === 0 ||
    filters.type.includes(DocumentTypeEnum.RECHNUNG);

  const { data, loading, error, fetchMore } = useQuery<{
    documents: DocumentConnection;
  }>(DOCUMENTS_QUERY, {
    variables: {
      filter: gqlFilter,
      sortBy: toSortField(sortConfig.key),
      sortDirection: toSortDirection(sortConfig.direction),
      first: PAGE_SIZE,
      withPositions: positionsView.active,
      ...adminQueryVars,
    },
    fetchPolicy: 'cache-and-network',
  });

  const [fetchBulkPdf, { loading: bulkPdfLoading }] = useLazyQuery<{
    documentBulkPdfUrl: string;
  }>(DOCUMENT_BULK_PDF_URL_QUERY, {
    fetchPolicy: 'network-only',
  });

  const [fetchExportDocs, { loading: exportLoading }] = useLazyQuery<{
    documents: DocumentConnection;
  }>(DOCUMENTS_EXPORT_QUERY, {
    // no-cache (nicht network-only): Der Export teilt sich den `documents`-
    // Cache-Eintrag mit der paginierten Liste (Field-Policy keyArgs ohne `first`),
    // selektiert aber kein `cursor`. network-only WÜRDE die cursor-losen Export-
    // Edges in den Cache schreiben → "Missing field 'cursor'". no-cache schreibt
    // gar nicht in den Cache; die Einweg-Export-Daten kommen rein über das Promise.
    fetchPolicy: 'no-cache',
  });

  const [fetchExportDocsWithLines, { loading: exportLinesLoading }] = useLazyQuery<{
    documentsForExport: Document[];
  }>(DOCUMENTS_FOR_EXPORT_QUERY, {
    fetchPolicy: 'network-only',
  });

  const documents = useMemo(
    () => data?.documents?.edges?.map((e) => e.node) ?? [],
    [data]
  );
  const pageInfo = data?.documents?.pageInfo;
  const totalCount = data?.documents?.totalCount ?? 0;
  // Tokens, die die Suchmaschine wirklich getroffen hat — markiert im Beleg
  // auch unscharfe Treffer ("PU-Schaum" → "PU-Klebeschaum").
  const searchMatches = data?.documents?.searchMatches ?? null;

  const allSelected = totalCount > 0 && sel.count >= totalCount;

  // Expliziter Auswahlmodus über den geteilten Hook. Der bestehende implizite
  // Einstieg (Desktop-Badge-Klick, mobiler Long-Press) aktiviert den Modus mit,
  // damit keine Funktion verloren geht.
  const handleToggleSelection = useCallback(
    (id: string) => {
      sel.enter();
      sel.toggle(id);
    },
    [sel.enter, sel.toggle],
  );

  // „Alle auswählen" lädt — anders als die rein lokalen Listen — ALLE passenden
  // Belege über alle Seiten (GraphQL, bis EXPORT_LIMIT). Bei bestehender Auswahl
  // hebt der Toggle sie wieder auf. Der Async-Fetch bleibt hier in der View; den
  // Ladezustand hält der Hook (`selectAllLoading`).
  const toggleAllSelection = useCallback(async () => {
    if (sel.count > 0) {
      sel.deselectAll();
      return;
    }
    sel.setSelectAllLoading(true);
    try {
      const result = await fetchExportDocs({
        variables: {
          filter: gqlFilter,
          sortBy: toSortField(sortConfig.key),
          sortDirection: toSortDirection(sortConfig.direction),
          first: EXPORT_LIMIT,
          ...adminQueryVars,
        },
      });
      const allDocs = result.data?.documents?.edges?.map((e) => e.node) ?? [];
      sel.setSelected(allDocs.map((d) => d.id));
    } finally {
      sel.setSelectAllLoading(false);
    }
  }, [sel, fetchExportDocs, gqlFilter, sortConfig, adminQueryVars]);

  /**
   * Apply selection filter to a fetched batch.
   * - When user has actively selected docs, export only those.
   * - When nothing is selected, fall back to all matching docs (existing behavior
   *   for desktop CSV/ZIP buttons that operate on the filtered set).
   */
  const filterBySelection = useCallback(
    (docs: Document[]) => {
      if (sel.count === 0) return docs;
      return docs.filter((d) => sel.selectedSet.has(d.id));
    },
    [sel.count, sel.selectedSet]
  );

  const selectedDocs = useMemo(
    () => documents.filter((d) => sel.selectedSet.has(d.id)),
    [documents, sel.selectedSet]
  );

  // ------------------------------------------------------------------
  // Pagination
  // ------------------------------------------------------------------
  const handleLoadMore = useCallback(() => {
    if (!pageInfo?.hasNextPage || !pageInfo?.endCursor) return;
    fetchMore({
      variables: {
        after: pageInfo.endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          documents: {
            ...fetchMoreResult.documents,
            edges: [
              ...prev.documents.edges,
              ...fetchMoreResult.documents.edges,
            ],
          },
        };
      },
    });
  }, [fetchMore, pageInfo]);

  // ------------------------------------------------------------------
  // Open / Close detail dialog
  // ------------------------------------------------------------------
  const handleOpenDocument = useCallback(
    (id: string) => {
      setOpenDocumentId(id);
      lastUrlOpenRef.current = id;
      const params = new URLSearchParams(searchParams.toString());
      params.set('open', id);
      router.push(`?${params.toString()}`, { scroll: false });
    },
    [router, searchParams]
  );

  const handleCloseDocument = useCallback(() => {
    setOpenDocumentId(null);
    lastUrlOpenRef.current = null;
    const params = new URLSearchParams(searchParams.toString());
    params.delete('open');
    const qs = params.toString();
    router.replace(qs ? `?${qs}` : '?', { scroll: false });
  }, [router, searchParams]);

  // When the open doc isn't in the current list page (e.g. user jumped to a
  // referenced document via TEXT-line linkify or a per-position jump button,
  // and that doc lies outside the active filter / pagination), fetch it on
  // its own. cache-first wins from the Apollo cache populated by
  // navigateToReferencedDocument's network-only search.
  const documentInListIds = useMemo(
    () => new Set(documents.map((d) => d.id)),
    [documents]
  );
  const singleDocQuery = useQuery<{ document: Document | null }>(
    DOCUMENT_QUERY,
    {
      variables: { id: openDocumentId, ...adminQueryVars },
      skip: !openDocumentId || documentInListIds.has(openDocumentId),
      fetchPolicy: 'cache-first',
    }
  );

  const openDocument = useMemo(
    () =>
      documents.find((d) => d.id === openDocumentId) ??
      singleDocQuery.data?.document ??
      null,
    [documents, openDocumentId, singleDocQuery.data]
  );

  // ------------------------------------------------------------------
  // Export actions
  // ------------------------------------------------------------------
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExport = useCallback(async () => {
    try {
      setExportError(null);
      const result = await fetchExportDocs({
        variables: {
          filter: gqlFilter,
          sortBy: toSortField(sortConfig.key),
          sortDirection: toSortDirection(sortConfig.direction),
          first: EXPORT_LIMIT,
          ...adminQueryVars,
        },
      });
      const allDocs = result.data?.documents?.edges?.map((e) => e.node) ?? [];
      const exportDocs = filterBySelection(allDocs);
      const filename = `Belege_Export_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.csv`;
      exportToCsv(exportDocs, filename);
    } catch {
      setExportError('Export fehlgeschlagen. Bitte erneut versuchen.');
    }
  }, [fetchExportDocs, gqlFilter, sortConfig, adminQueryVars, filterBySelection]);

  const handleExportLines = useCallback(async () => {
    try {
      setExportError(null);
      const result = await fetchExportDocsWithLines({
        variables: {
          filter: gqlFilter,
          sortBy: toSortField(sortConfig.key),
          sortDirection: toSortDirection(sortConfig.direction),
          first: EXPORT_LIMIT,
          ...adminQueryVars,
        },
      });
      const allDocs = result.data?.documentsForExport ?? [];
      const exportDocs = filterBySelection(allDocs);
      const filename = `Belege_Positionen_Export_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.csv`;
      exportLineItemsToCsv(exportDocs, filename);
    } catch {
      setExportError('Export fehlgeschlagen. Bitte erneut versuchen.');
    }
  }, [fetchExportDocsWithLines, gqlFilter, sortConfig, adminQueryVars, filterBySelection]);

  // ZIP download must include ALL selected documents across pagination, not just the loaded page.
  // Fetch the full filtered set (same pattern as CSV export), then hand IDs to the bulk PDF endpoint.
  const handleDownloadAll = useCallback(async () => {
    try {
      setExportError(null);
      const result = await fetchExportDocs({
        variables: {
          filter: gqlFilter,
          sortBy: toSortField(sortConfig.key),
          sortDirection: toSortDirection(sortConfig.direction),
          first: EXPORT_LIMIT,
          ...adminQueryVars,
        },
      });
      const allDocs = result.data?.documents?.edges?.map((e) => e.node) ?? [];
      const selected = filterBySelection(allDocs);
      const docIds = selected.filter((d) => d.pdfKey).map((d) => d.id);

      if (docIds.length === 0) {
        setExportError('Keine herunterladbaren PDFs in der Auswahl.');
        return;
      }
      // Backend enforces the same cap (DocumentPdfService.getBulkPdfUrl) —
      // pre-check here gives a friendlier message than a 400.
      if (docIds.length > 100) {
        setExportError(
          'ZIP-Download ist auf 100 Belege begrenzt. Bitte Filter verfeinern.'
        );
        return;
      }

      const bulk = await fetchBulkPdf({
        variables: { documentIds: docIds, ...adminQueryVars },
      });
      if (bulk.data?.documentBulkPdfUrl) {
        window.open(bulk.data.documentBulkPdfUrl, '_blank');
      }
    } catch {
      setExportError('ZIP-Download fehlgeschlagen. Bitte erneut versuchen.');
    }
  }, [
    fetchExportDocs,
    gqlFilter,
    sortConfig,
    adminQueryVars,
    filterBySelection,
    fetchBulkPdf,
  ]);

  // Bulk-Aktion: ausgewählte Belege als gelesen/ungelesen markieren.
  // Optimistic update aktualisiert die normalisierten Document-Nodes sofort
  // (Listenzeilen schalten um); DashboardStats wird (falls gemountet) refetcht.
  const [setDocumentsRead, { loading: markLoading }] = useMutation(
    SET_DOCUMENTS_READ_MUTATION,
  );

  const handleMarkSelected = useCallback(
    async (read: boolean) => {
      const ids = Array.from(sel.selectedSet);
      if (ids.length === 0) return;
      try {
        await setDocumentsRead({
          variables: { documentIds: ids, read, ...adminQueryVars },
          optimisticResponse: {
            setDocumentsRead: ids.map((id) => ({
              __typename: 'Document',
              id,
              isRead: read,
            })),
          },
          refetchQueries: ['DashboardStats'],
        });
        sel.exit();
      } catch {
        // Apollo-Cache rollt das optimistische Update bei Fehler automatisch zurück.
      }
    },
    [sel, setDocumentsRead, adminQueryVars],
  );

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  if (error) {
    // Eigene Seite, eigener Handlungsaufruf.
    if (needsRenewal) {
      return <SessionRenewalNotice showAction className="m-6" />;
    }
    return (
      <div className="p-12 text-center">
        <p className="text-red-600 font-medium mb-2">
          Fehler beim Laden der Belege
        </p>
        <p className="text-gray-500 text-sm">{error.message}</p>
      </div>
    );
  }

  return (
    <SearchHighlightProvider searchTerm={filters.search} matches={searchMatches}>
    <div className="space-y-6">
      <DocumentFilters
        filters={filters}
        onFilterChange={setFilters}
        sortConfig={sortConfig}
        onSortChange={(next) => updateUrl(filters, next)}
      />

      {/* Controls-Zeile nach Design „Belegseite (gefiltert)": links „Filter
          aktiv" (oben) und der Auswahl-Umschalter „Auswählen" (unten, an der
          Unterkante der Card ausgerichtet), rechts die kompakte
          Rechnungssummen-Card. Im Auswahl-Modus erscheinen die „Alle
          auswählen"-Zeile + die Aktionsleiste sofort; beendet wird der Modus
          über den „Fertig"-Button daneben oder das X der Leiste. */}
      <div className="flex flex-col md:flex-row md:items-stretch md:justify-between gap-4 md:gap-7">
        <div className="order-2 md:order-1 flex flex-col items-start gap-4 min-w-0 md:pt-2">
          <DocumentFilterChips filters={filters} onFilterChange={setFilters} />

          {totalCount > 0 && (
            <div className="flex flex-wrap items-center gap-3 md:mt-auto">
              {!sel.selectMode && (
                <button
                  type="button"
                  onClick={sel.enter}
                  aria-label="Belege auswählen"
                  className="inline-flex items-center gap-2 h-9 px-3.5 rounded-lg border border-zinc-200 bg-white text-zinc-600 text-sm font-semibold hover:bg-zinc-50 transition-colors"
                >
                  <CheckSquare size={16} />
                  Auswählen
                </button>
              )}

              {sel.selectMode && (
                <SelectAllToggle
                  checked={allSelected}
                  indeterminate={sel.count > 0 && sel.count < totalCount}
                  loading={sel.selectAllLoading}
                  onToggle={toggleAllSelection}
                  countSuffix={
                    <>
                      · {totalCount} {totalCount === 1 ? 'Beleg' : 'Belege'}
                    </>
                  }
                  loadingLabel="Lade alle Belege …"
                  onExit={sel.exit}
                />
              )}

              {filters.search && !sel.selectMode && (
                <PositionsToggle
                  active={positionsView.active}
                  onToggle={handleTogglePositions}
                />
              )}
            </div>
          )}
        </div>

        {showInvoiceTotals && (
          <div className="order-1 md:order-2 w-full md:w-auto md:flex-1 flex md:justify-end">
            <InvoiceTotalsBar filter={gqlFilter} />
          </div>
        )}
      </div>

      {/* Aktionsleiste (mobil + Desktop) = geteilte SelectionBar via
          <BelegeExportBar /> weiter unten. */}

      <div className="md:bg-white md:rounded-xl md:shadow-sm md:border md:border-gray-200 md:overflow-hidden">
        {/* Table Header */}
        <div
          className="hidden md:grid grid-cols-1 gap-4 p-4 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider"
        >
          <div className="grid grid-cols-12 gap-4 w-full">
            <div className="col-span-3">
              <HeaderCell
                label="Beleg"
                columnKey="number"
                sortConfig={sortConfig}
                onSort={handleSort}
              />
            </div>
            <div className="col-span-2">
              <HeaderCell
                label="Datum"
                columnKey="date"
                sortConfig={sortConfig}
                onSort={handleSort}
              />
            </div>
            <div className="col-span-4">
              <HeaderCell
                label="Kommission / Referenz"
                columnKey="commission"
                sortConfig={sortConfig}
                onSort={handleSort}
              />
            </div>
            <div className="col-span-3 text-right">
              <HeaderCell
                label="Betrag"
                columnKey="netTotal"
                alignRight
                sortConfig={sortConfig}
                onSort={handleSort}
              />
            </div>
          </div>
        </div>

        {/* Loading skeleton */}
        {loading && documents.length === 0 && (
          <div className="divide-y divide-gray-100">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="p-4">
                <div className="grid grid-cols-12 gap-4">
                  <Skeleton className="col-span-3 h-5 rounded" />
                  <Skeleton className="col-span-2 h-5 rounded" />
                  <Skeleton className="col-span-4 h-5 rounded" />
                  <Skeleton className="col-span-3 h-5 rounded" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Document rows */}
        {!loading && documents.length > 0 && (
          <div>
            {documents.map((doc) => (
              <DocumentRow
                key={doc.id}
                document={doc}
                isSelected={sel.isSelected(doc.id)}
                selectionMode={sel.selectMode}
                onToggleSelection={handleToggleSelection}
                onOpen={() => handleOpenDocument(doc.id)}
              />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && documents.length === 0 && (
          <EmptyState
            icon={FileX}
            title="Keine Belege gefunden"
            description="Für diese Suchkriterien wurden keine Belege gefunden."
            action={{
              label: 'Filter zurücksetzen',
              onClick: () =>
                setFilters({
                  search: '',
                  type: [],
                  dateRange: 'ALL',
                  startDate: undefined,
                  endDate: undefined,
                  minAmount: '',
                  maxAmount: '',
                  location: '',
                  commission: '',
                  yourReference: '',
                }),
            }}
          />
        )}
      </div>

      {/* Load More */}
      {pageInfo?.hasNextPage && (
        <div className="flex justify-center pt-4">
          <button
            onClick={handleLoadMore}
            disabled={loading}
            className="px-6 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm disabled:opacity-50"
          >
            {loading
              ? 'Laden...'
              : `Mehr laden (${documents.length} von ${totalCount})`}
          </button>
        </div>
      )}

      <BelegeExportBar
        count={sel.count}
        open={sel.selectMode}
        onClear={sel.exit}
        onMarkRead={() => handleMarkSelected(true)}
        onMarkUnread={() => handleMarkSelected(false)}
        onDownloadZip={handleDownloadAll}
        onExportCsv={handleExport}
        onExportLines={handleExportLines}
        zipLoading={bulkPdfLoading}
        csvLoading={exportLoading || exportLinesLoading}
        markLoading={markLoading}
        exportError={exportError}
      />

      <DocumentDetailDialog
        document={openDocument}
        isOpen={!!openDocument}
        onClose={handleCloseDocument}
      />
    </div>
    </SearchHighlightProvider>
  );
}
